/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
import { Container, interfaces } from 'inversify'
import { rootContainer } from '~/container'
import sentryCapture from '~/utility/sentryCapture'
import { isAbortErrorFromEvent } from '~/utility/isAbortError'
import { langFallback$Fetch } from '../../lang/utils/langfetch'
import { logger } from '../../utility/logger'
import { getAuthService } from '../auth/service'
import { csrfTokenService } from '../csrf/CsrfTokenService'
import { assign, ErrorPlatformEvent, interpret } from 'xstate-v4'
import { BookingDataServiceFetch } from './data/fetch'
import { bookingDataEndpoint } from './data/schema'
import { BookingService, returnBookingMachine } from './machines/booking'
import { BookingContext } from './machines/types'
import { PayPalCallbackServiceFetch } from './paypal/fetch'
import {
  PayPalProcessingService,
  PayPalProcessingStateContext,
  payPalProcessingMachine,
} from './paypal/machine'
import { paypalCallbackEndpoint } from './paypal/schema'
import { PayPalCallbackService } from './paypal/services'
import { QuoteRepositoryFetch } from './quote/fetch'
import { quoteRepositoryEndpoint } from './quote/schema'
import { BookingStatusServiceFetch } from './status/fetch'
import { bookingStatusEndpoint, bookingStatusParamsZ } from './status/schema'
import { BookingStoredCardsServiceFetch } from './stored-cards/fetch'
import {
  returnSavedPaymentCardsStateMachine,
  savedPaymentCardsMachineInterpreter,
} from './stored-cards/machine'
import { BookingSubmitServiceFetch } from './submit/fetch'
import { BookingSubmitService } from './submit/services'
import { getURLBody } from './utils'

export const bookingContainer = new Container()
bookingContainer.parent = rootContainer

bookingContainer.bind(PayPalCallbackService).to(PayPalCallbackServiceFetch)
bookingContainer.bind(BookingSubmitService).to(BookingSubmitServiceFetch)

bookingContainer
  .bind<interfaces.Factory<BookingService>>('Factory<BookingService>')
  .toFactory<BookingService>((context: interfaces.Context) => {
    return (ctx: BookingContext) => {
      const quoteRepository = quoteRepositoryEndpoint.validate((args) =>
        new QuoteRepositoryFetch().getQuote(args, ctx.langCode),
      )
      const dataService = bookingDataEndpoint.validate((args) =>
        new BookingDataServiceFetch().get(args, ctx.langCode),
      )
      const machine = returnBookingMachine()
      return interpret(
        machine.withContext({ ...machine.context, ...ctx }).withConfig({
          actions: {
            checkUserStore: assign((_) => {
              const authMachine = getAuthService()
              const email = authMachine.state.context.email
              return {
                loggedInUserEmail: email || '',
                isAuthenticated: !!email,
              }
            }),
            logError: (_, _event: unknown) => {
              const event: ErrorPlatformEvent = _event as ErrorPlatformEvent

              if (isAbortErrorFromEvent(event)) {
                return
              }

              event.data.name = `bookingMachine ${event.type} error`
              sentryCapture(event.data)
            },
            logUnknownError: (_, event) => {
              if (isAbortErrorFromEvent(event)) {
                return
              }
              // @ts-expect-error - event.data type is not properly typed in XState error events
              sentryCapture(event.data)
            },
          },
          services: {
            loadData: ({ requestParams, selectedExtras }) => {
              return dataService({
                ...requestParams,
                selectedExtras: Object.keys(selectedExtras).length
                  ? selectedExtras
                  : requestParams.selectedExtras,
              })
            },
            loadQuote: ({ requestParams, selectedExtras }) => {
              selectedExtras = selectedExtras || {}
              return quoteRepository({
                ...requestParams,
                selectedExtras: Object.keys(selectedExtras).length
                  ? selectedExtras
                  : requestParams.selectedExtras,
              })
            },
            submitForm: ({ bookingRequest }) => {
              logger('booking.machine submitForm', bookingRequest)
              if (!bookingRequest) {
                throw new Error('Missing bookingRequest')
              }
              try {
                return context.container
                  .get(BookingSubmitService)
                  .submit(bookingRequest)
              } catch (e) {
                console.error(bookingRequest, e)
                throw e
              }
            },
            checkEmailService: async ({ email }) =>
              await $fetch(`/_/fallback/account/email_check/?email=${email}`),
            registerInterest: async ({ contactDetails, requestParams }) => {
              const { csrfToken } = await csrfTokenService()
              const data = {
                adults: requestParams.party.adults,
                arrive: requestParams.stayRange.start,
                child_ages: requestParams.party.childAges.join(','),
                depart: requestParams.stayRange.end,
                email: contactDetails!.email,
                pitchtype_id: requestParams.pitchtypeId,
              }
              return langFallback$Fetch(
                ctx.langCode,
                '/_/booking/register-interest/',
                {
                  method: 'POST',
                  data: getURLBody(data),
                  headers: {
                    'X-CSRFToken': csrfToken,
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                },
              )
            },
          },
        }),
      )
    }
  })

bookingContainer
  .bind<
    interfaces.Factory<PayPalProcessingService>
  >('Factory<PayPalProcessingService>')
  .toFactory<PayPalProcessingService>((context: interfaces.Context) => {
    return (ctx: PayPalProcessingStateContext) => {
      const payPalCallback = paypalCallbackEndpoint.validate((params) =>
        context.container.get(PayPalCallbackService).get(params, ctx.langCode),
      )
      const loadStatus = (params: PayPalProcessingStateContext) =>
        bookingStatusEndpoint.validate((args) =>
          new BookingStatusServiceFetch().get(args, ctx.langCode),
        )(bookingStatusParamsZ.parse(params))
      return interpret(
        payPalProcessingMachine.withContext(ctx).withConfig({
          services: {
            triggerPayPalCheckingTask: (params) => payPalCallback(params),
            loadStatus: (params) => loadStatus(params),
          },
        }),
      )
    }
  })

bookingContainer
  .bind<savedPaymentCardsMachineInterpreter>('SavedPaymentCardsStateMachine')
  .toDynamicValue(() =>
    interpret(
      returnSavedPaymentCardsStateMachine().withConfig({
        services: {
          loadSavedCards: () => new BookingStoredCardsServiceFetch().readAll(),
        },
      }),
    ),
  )

export function getSavedPaymentCardsMachine() {
  return bookingContainer.get<savedPaymentCardsMachineInterpreter>(
    'SavedPaymentCardsStateMachine',
  )
}

export function getPayPalProcessingFactory() {
  return bookingContainer.get<interfaces.Factory<PayPalProcessingService>>(
    'Factory<PayPalProcessingService>',
  )
}
